<script setup lang="ts">
  import { ref, onMounted, watch, computed, getCurrentInstance } from 'vue';
  import { useIntersectionObserver } from '@vueuse/core';
  import StatsService from '@/api/services/stats.service';
  import V2LineChart from './V2LineChart.vue';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { roundPrice } from '@/utils';
  import { StatsInterval } from '@/enums/StatsInterval';
  import {
    IBusinessDayAnchor,
    businessHourOrder,
    getTodayBucketCount,
  } from '@/domain/stats/business-day';
  import {
    TDashboardTimeframe,
    getDashboardComparisonDay,
    getDashboardComparisonRange,
    getDashboardRange,
    isHourlyTimeframe,
    parseStatDayKey,
    toStatDayKey,
  } from '@/domain/dashboard/dashboard-timeframes';
  import {
    bucketStatsByBusinessHour,
    bucketStatsByDay,
    sumStatGroupsByBucket,
  } from '@/domain/dashboard/dashboard-chart-buckets';

  const props = defineProps<{
    /** Branch business day resolved by the dashboard page (null = pending). */
    businessDay: IBusinessDayAnchor | null;
  }>();

  const restaurantStore = useRestaurantStore();

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  // Services
  const statsService = new StatsService();

  // State
  const loading = ref(true);
  const hasLoadedOnce = ref(false);
  const chartContentRef = ref<HTMLElement>();
  const isInView = ref(false);
  const selectedMetric = ref('revenue');
  const selectedTimeframe = ref<TDashboardTimeframe>('7days');
  const showComparison = ref(true);
  // Latest load wins: slower stale responses never overwrite newer data.
  let loadSeq = 0;
  // Non-monetary amounts (invitaciones / consumo empleado) excluded per data
  // point when the revenue metric is shown; aligned with chartData.labels.
  // Reset on every load so other metrics never see stale revenue comps.
  const currentCompsData = ref<number[]>([]);
  const comparisonCompsData = ref<number[]>([]);
  // Raw yyyyMMdd dates behind chartData.labels (multi-day timeframes only,
  // empty for hourly ones). Needed by the tooltip title callback to show the
  // real (shifted) date when hovering a comparison-dataset point.
  const currentSortedDates = ref<string[]>([]);
  const chartData = ref({
    labels: [],
    datasets: [],
  });
  const chartOptions = ref({
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        fontColor: '#6b7280',
        fontSize: 12,
        padding: 10,
        usePointStyle: true,
      },
    },
    scales: {
      xAxes: [
        {
          gridLines: {
            display: false,
          },
          ticks: {
            fontColor: '#6b7280',
            fontSize: 12,
            // Skip ticks instead of rotating them when labels don't fit
            // (weekday-prefixed dates are wider than plain DD/MM).
            maxRotation: 0,
            autoSkipPadding: 12,
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            color: '#f3f4f6',
            borderDash: [2, 2],
          },
          ticks: {
            fontColor: '#6b7280',
            fontSize: 12,
            beginAtZero: true,
            callback: function (value) {
              if (selectedMetric.value === 'revenue') {
                return (
                  (restaurantStore.currency?.symbol || '$') + value.toFixed(0)
                );
              }
              return value;
            },
          },
        },
      ],
    },
    tooltips: {
      backgroundColor: '#1f2937',
      titleFontSize: 12,
      bodyFontSize: 14,
      cornerRadius: 4,
      displayColors: true,
      callbacks: {
        // Both datasets share the current period's x-axis labels, so for a
        // comparison-only tooltip the default title (xLabel) would show the
        // current period's date. Show the comparison period's real date.
        title: function (tooltipItems) {
          const item = tooltipItems[0];
          if (!item) return '';
          const date = currentSortedDates.value[item.index];
          const comparisonOnly = tooltipItems.every(
            (t) => t.datasetIndex === 1
          );
          if (!comparisonOnly || !date) return item.xLabel;
          const comparisonDate = comparisonDateFor(date);
          return comparisonDate ? formatDateLabel(comparisonDate) : item.xLabel;
        },
        label: function (tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          const value = tooltipItem.value;
          if (selectedMetric.value === 'revenue') {
            const symbol = restaurantStore.currency?.symbol || '$';
            const line = label + ': ' + symbol + value;
            const compsArray =
              tooltipItem.datasetIndex === 0
                ? currentCompsData.value
                : comparisonCompsData.value;
            const comps = compsArray[tooltipItem.index] || 0;
            if (comps > 0) {
              // Chart.js v2 renders string arrays as multi-line labels.
              return [
                line,
                String(
                  proxy.$t('dashboard.nonMonetaryTooltipLine', {
                    amount: `${symbol}${comps.toFixed(2)}`,
                  })
                ),
              ];
            }
            return line;
          }
          return label + ': ' + value;
        },
      },
    },
  });

  // Computed
  const metricConfig = computed(() => {
    switch (selectedMetric.value) {
      case 'orders':
        return {
          method: 'orders',
          groupId: 'count',
          label: String(proxy.$t('dashboard.metricOrders')),
          color: '#ff6f00',
          borderColor: '#ff6f00',
          backgroundColor: 'rgba(255, 111, 0, 0.1)',
        };
      case 'revenue':
        return {
          method: 'orders',
          groupId: 'total',
          label: String(proxy.$t('dashboard.metricRevenue')),
          color: '#10b981',
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
        };
      case 'views':
        return {
          method: 'views',
          label: String(proxy.$t('dashboard.metricViews')),
          color: '#3b82f6',
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
        };
      default:
        return null;
    }
  });

  const timeframeOptions = computed(() => [
    { text: String(proxy.$t('dashboard.tfToday')), value: 'today' },
    { text: String(proxy.$t('dashboard.tfYesterday')), value: 'yesterday' },
    { text: String(proxy.$t('dashboard.tfLast7')), value: '7days' },
    { text: String(proxy.$t('dashboard.tfLast4w')), value: '4weeks' },
    {
      text: String(proxy.$t('dashboard.tfThisMonthToDate')),
      value: 'thisMonth',
    },
  ]);

  // Computed for totals (comparison points can be null: gaps for days that
  // do not exist in the previous month)
  const currentPeriodTotal = computed(() => {
    if (!chartData.value.datasets.length) return 0;
    const currentDataset = chartData.value.datasets[0];
    if (!currentDataset || !currentDataset.data) return 0;
    return currentDataset.data.reduce((sum, value) => sum + (value ?? 0), 0);
  });

  const comparisonPeriodTotal = computed(() => {
    if (!showComparison.value || chartData.value.datasets.length < 2) return 0;
    const comparisonDataset = chartData.value.datasets[1];
    if (!comparisonDataset || !comparisonDataset.data) return 0;
    return comparisonDataset.data.reduce((sum, value) => sum + (value ?? 0), 0);
  });

  const percentageChange = computed(() => {
    if (!showComparison.value || comparisonPeriodTotal.value === 0) return null;
    return (
      ((currentPeriodTotal.value - comparisonPeriodTotal.value) /
        comparisonPeriodTotal.value) *
      100
    );
  });

  const formattedTotal = computed(() => {
    if (selectedMetric.value === 'revenue') {
      return `${
        restaurantStore.currency?.symbol || '$'
      }${currentPeriodTotal.value.toFixed(2)}`;
    }
    return currentPeriodTotal.value.toString();
  });

  const currentCompsTotal = computed(() =>
    currentCompsData.value.reduce((sum, value) => sum + value, 0)
  );

  // Double gate (metric + amount): comps refs are reset on every load, but
  // the summary card renders for all metrics.
  const showSummaryCompsInfo = computed(
    () => selectedMetric.value === 'revenue' && currentCompsTotal.value > 0
  );

  const summaryCompsInfoText = computed(() => {
    const symbol = restaurantStore.currency?.symbol || '$';
    return String(
      proxy.$t('dashboard.revenueNonMonetaryInfo', {
        amount: `${symbol}${currentCompsTotal.value.toFixed(2)}`,
        total: `${symbol}${(
          currentPeriodTotal.value + currentCompsTotal.value
        ).toFixed(2)}`,
      })
    );
  });

  // Set up intersection observer
  const { stop } = useIntersectionObserver(
    chartContentRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !hasLoadedOnce.value) {
        isInView.value = true;
        loadChartData();
        stop(); // Stop observing once loaded
      }
    },
    {
      threshold: 0.1, // Trigger when 10% of the element is visible
    }
  );

  // Methods

  // Localized short weekday names (e.g. "lun"), cached per locale.
  const weekdayFormatter = computed(
    () => new Intl.DateTimeFormat(proxy.$i18n.locale, { weekday: 'short' })
  );

  // Formats a yyyyMMdd date as "lun 22/08" (chart axis / tooltip title
  // format). Some locales abbreviate with a trailing dot ("seg."); strip it
  // to keep axis labels compact.
  const formatDateLabel = (date: string): string => {
    const weekday = weekdayFormatter.value
      .format(parseStatDayKey(date))
      .replace(/\.$/, '');
    return `${weekday} ${date.substring(6, 8)}/${date.substring(4, 6)}`;
  };

  // Maps a current-period yyyyMMdd date to its comparison-period equivalent
  // (null when that day does not exist in the previous month).
  const comparisonDateFor = (date: string): string | null => {
    const day = getDashboardComparisonDay(
      selectedTimeframe.value,
      parseStatDayKey(date)
    );
    return day ? toStatDayKey(day) : null;
  };

  const loadChartData = async () => {
    if (!isInView.value) return; // Don't load if not in view
    const anchor = props.businessDay;
    // The page is still resolving the business day; its watcher loads later.
    if (!anchor) return;
    const seq = ++loadSeq;

    loading.value = true;
    hasLoadedOnce.value = true;

    // Always reset: the metric watcher re-runs this, and stale revenue comps
    // must never leak into the orders/views metrics. Dates reset too so the
    // tooltip title never maps hourly indexes through stale daily dates.
    currentCompsData.value = [];
    comparisonCompsData.value = [];
    currentSortedDates.value = [];

    const timeframe = selectedTimeframe.value;
    const hourly = isHourlyTimeframe(timeframe);
    const range = getDashboardRange(timeframe, anchor.today);
    const comparisonRange = getDashboardComparisonRange(timeframe, range);

    try {
      const config = metricConfig.value;
      if (!config) return;

      const isRevenue = selectedMetric.value === 'revenue';

      // Load current period data (single-day timeframes send no "to": the
      // API answers them hourly along the business day)
      let currentStats;
      if (config.method === 'orders') {
        currentStats = await statsService.getOrdersStats(
          anchor.restaurantId,
          anchor.branchId,
          range.from,
          hourly ? undefined : range.to,
          undefined,
          undefined,
          undefined,
          isRevenue
        );
      } else {
        currentStats = await statsService.getViewsStats(
          anchor.restaurantId,
          anchor.branchId,
          range.from,
          hourly ? undefined : range.to
        );
      }

      // Load comparison period data if enabled
      let comparisonStats = null;
      if (showComparison.value) {
        if (config.method === 'orders') {
          comparisonStats = await statsService.getOrdersStats(
            anchor.restaurantId,
            anchor.branchId,
            comparisonRange.from,
            hourly ? undefined : comparisonRange.to,
            undefined,
            undefined,
            undefined,
            isRevenue
          );
        } else {
          comparisonStats = await statsService.getViewsStats(
            anchor.restaurantId,
            anchor.branchId,
            comparisonRange.from,
            hourly ? undefined : comparisonRange.to
          );
        }
      }

      // A newer load started while these were in flight.
      if (seq !== loadSeq) return;

      if (!currentStats || !currentStats.groups) {
        chartData.value = { labels: [], datasets: [] };
        return;
      }

      let currentGroup;
      let comparisonGroup;

      // Special handling for views - a synthetic group summing all QR types
      if (config.method === 'views') {
        currentGroup = {
          id: 'total',
          stats: sumStatGroupsByBucket(currentStats.groups),
        };

        if (comparisonStats?.groups) {
          comparisonGroup = {
            id: 'total',
            stats: sumStatGroupsByBucket(comparisonStats.groups),
          };
        }
      } else {
        // Original logic for orders/revenue
        currentGroup = currentStats.groups.find((g) => g.id === config.groupId);
        comparisonGroup = comparisonStats?.groups?.find(
          (g) => g.id === config.groupId
        );
      }

      if (!currentGroup || !currentGroup.stats) {
        chartData.value = { labels: [], datasets: [] };
        return;
      }

      // Non-monetary (comps) groups, only requested for the revenue metric.
      // ALWAYS looked up by id, never by index.
      const currentCompsGroup = isRevenue
        ? currentStats.groups.find((g) => g.id === 'nonMonetaryTotal')
        : undefined;
      const comparisonCompsGroup = isRevenue
        ? comparisonStats?.groups?.find((g) => g.id === 'nonMonetaryTotal')
        : undefined;

      // Process data based on timeframe
      let labels: string[] = [];
      let currentData: number[] = [];
      // null = gap: the comparison day does not exist (29-31 of a month
      // whose previous month is shorter)
      let comparisonData: (number | null)[] = [];
      let currentComps: number[] = [];
      let comparisonComps: number[] = [];

      if (hourly) {
        // Hours along the business day (start hour first, wrapping past
        // midnight). Today: up to the current hour (the whole day once the
        // anchored day is over); yesterday: all 24 hours.
        const count = timeframe === 'today' ? getTodayBucketCount(anchor) : 24;
        const cutoffHour = anchor.cutoffHour;
        labels = businessHourOrder(cutoffHour)
          .slice(0, count)
          .map((hour) => `${hour}:00`);
        currentData = bucketStatsByBusinessHour(
          currentGroup.stats,
          cutoffHour,
          count
        );
        comparisonData = bucketStatsByBusinessHour(
          comparisonGroup?.stats || [],
          cutoffHour,
          count
        );

        if (currentCompsGroup?.stats) {
          currentComps = bucketStatsByBusinessHour(
            currentCompsGroup.stats,
            cutoffHour,
            count
          );
        }
        if (comparisonCompsGroup?.stats) {
          comparisonComps = bucketStatsByBusinessHour(
            comparisonCompsGroup.stats,
            cutoffHour,
            count
          );
        }
      } else {
        // For multi-day views, aggregate by day. A single-day range ("Este
        // mes" on the 1st) is answered hourly: its buckets all belong to
        // the range's day, even the post-midnight ones dated the next day.
        const currentDayKey =
          currentStats.interval === StatsInterval.Hourly
            ? toStatDayKey(range.from)
            : undefined;
        const comparisonDayKey =
          comparisonStats?.interval === StatsInterval.Hourly
            ? toStatDayKey(comparisonRange.from)
            : undefined;
        const currentDailyData = bucketStatsByDay(
          currentGroup.stats,
          currentDayKey
        );

        // Sort dates and create labels
        const sortedDates = Object.keys(currentDailyData).sort();
        labels = sortedDates.map(formatDateLabel);
        currentData = sortedDates.map((date) => currentDailyData[date]);
        currentSortedDates.value = sortedDates;

        // Map comparison data to same day positions
        if (showComparison.value && comparisonGroup) {
          const comparisonDailyData = bucketStatsByDay(
            comparisonGroup.stats,
            comparisonDayKey
          );
          comparisonData = sortedDates.map((date) => {
            const comparisonDate = comparisonDateFor(date);
            return comparisonDate === null
              ? null
              : comparisonDailyData[comparisonDate] || 0;
          });
        }

        if (currentCompsGroup?.stats) {
          const compsDailyData = bucketStatsByDay(
            currentCompsGroup.stats,
            currentDayKey
          );
          currentComps = sortedDates.map((date) => compsDailyData[date] || 0);
        }
        if (showComparison.value && comparisonCompsGroup?.stats) {
          const compsDailyData = bucketStatsByDay(
            comparisonCompsGroup.stats,
            comparisonDayKey
          );
          comparisonComps = sortedDates.map((date) => {
            const comparisonDate = comparisonDateFor(date);
            return comparisonDate === null
              ? 0
              : compsDailyData[comparisonDate] || 0;
          });
        }
      }

      // Revenue metric: plot NET values (gross − comps, clamped at 0) in
      // both datasets and keep the comps aligned with labels for tooltips.
      if (isRevenue) {
        currentData = currentData.map((value, i) =>
          Math.max(0, roundPrice(value - (currentComps[i] || 0)))
        );
        comparisonData = comparisonData.map((value, i) =>
          value === null
            ? null
            : Math.max(0, roundPrice(value - (comparisonComps[i] || 0)))
        );
        currentCompsData.value = currentComps;
        comparisonCompsData.value = comparisonComps;
      }

      // Build datasets
      const datasets: any[] = [
        {
          label: String(
            selectedTimeframe.value === 'today'
              ? proxy.$t('dashboard.tfToday')
              : selectedTimeframe.value === 'yesterday'
              ? proxy.$t('dashboard.tfYesterday')
              : selectedTimeframe.value === 'thisMonth'
              ? proxy.$t('dashboard.dsThisMonth')
              : proxy.$t('dashboard.dsCurrentPeriod')
          ),
          data: currentData,
          borderColor: config.borderColor,
          backgroundColor: config.backgroundColor,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.1,
        },
      ];

      if (showComparison.value && comparisonData.length > 0) {
        datasets.push({
          label: String(
            selectedTimeframe.value === 'today' ||
              selectedTimeframe.value === 'yesterday'
              ? proxy.$t('dashboard.dsSameDayLastWeek')
              : selectedTimeframe.value === 'thisMonth'
              ? proxy.$t('dashboard.dsPrevMonth')
              : selectedTimeframe.value === '7days'
              ? proxy.$t('dashboard.dsPrevPeriod7')
              : proxy.$t('dashboard.dsPrevPeriod4w')
          ),
          data: comparisonData,
          borderColor: '#9ca3af',
          backgroundColor: 'rgba(156, 163, 175, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.1,
        });
      }

      chartData.value = { labels, datasets };
    } catch (error) {
      if (seq !== loadSeq) return;
      console.error('Error loading chart data:', error);
      chartData.value = { labels: [], datasets: [] };
    } finally {
      if (seq === loadSeq) loading.value = false;
    }
  };

  // Watchers (a new business day = branch switch or page re-anchor)
  watch(
    [
      selectedMetric,
      selectedTimeframe,
      showComparison,
      () => props.businessDay,
      // Dataset labels are baked in at load time, so a locale switch must
      // rebuild the chart data for the legend to re-render translated.
      () => proxy.$i18n.locale,
    ],
    () => {
      if (isInView.value) {
        loadChartData();
      }
    }
  );

  // Lifecycle
  onMounted(() => {
    // Remove loadChartData() from here - it will be triggered by intersection observer
  });
</script>

<template>
  <v-card class="performance-chart-card" elevation="0">
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-chart-line</v-icon>
          <span class="header-title">{{ $t('dashboard.performance') }}</span>
        </div>
        <div class="header-right">
          <v-btn
            text
            small
            :to="`/${restaurantStore.restaurant?.stringId}/stats/${
              selectedMetric === 'views'
                ? 'views'
                : selectedMetric === 'revenue'
                ? 'paymentMethods'
                : 'orders'
            }`"
            class="view-more-btn"
          >
            {{ $t('dashboard.viewMore') }}
            <v-icon small right>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="card-content" ref="chartContentRef">
      <!-- Show skeleton while not loaded -->
      <div v-if="!hasLoadedOnce">
        <!-- Controls skeleton -->
        <div class="chart-controls-skeleton">
          <v-skeleton-loader type="button-group" width="250" />
          <div class="controls-right">
            <v-skeleton-loader type="checkbox" width="100" />
            <v-skeleton-loader type="select" width="180" />
          </div>
        </div>

        <!-- Summary skeleton -->
        <div class="summary-section">
          <v-skeleton-loader type="card" width="150" height="60" />
        </div>

        <!-- Chart skeleton -->
        <div class="chart-container">
          <v-skeleton-loader type="image" height="300" />
        </div>
      </div>

      <!-- Actual content - shown after first load -->
      <div v-else>
        <!-- Controls -->
        <div class="chart-controls">
          <v-btn-toggle
            v-model="selectedMetric"
            mandatory
            dense
            class="metric-toggle"
          >
            <v-btn value="revenue" small>
              {{ $t('dashboard.metricRevenue') }}
            </v-btn>
            <v-btn value="orders" small>
              {{ $t('dashboard.metricOrders') }}
            </v-btn>
            <v-btn value="views" small>
              {{ $t('dashboard.metricViews') }}
            </v-btn>
          </v-btn-toggle>

          <div class="controls-right">
            <v-checkbox
              v-model="showComparison"
              :label="String($t('dashboard.compare'))"
              hide-details
              dense
              class="comparison-toggle"
            />

            <v-select
              v-model="selectedTimeframe"
              :items="timeframeOptions"
              dense
              outlined
              hide-details
              class="timeframe-select"
            />
          </div>
        </div>

        <!-- Summary Section -->
        <div
          v-if="!loading && chartData.datasets.length"
          class="summary-section"
        >
          <div class="summary-card">
            <div class="summary-content">
              <div class="summary-value">
                {{ formattedTotal }}
                <v-tooltip v-if="showSummaryCompsInfo" bottom max-width="280">
                  <template #activator="{ on, attrs }">
                    <v-icon
                      small
                      class="summary-info-icon"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ summaryCompsInfoText }}</span>
                </v-tooltip>
              </div>
              <div
                v-if="showComparison && percentageChange !== null"
                class="summary-change"
              >
                <v-icon
                  small
                  :color="percentageChange >= 0 ? 'success' : 'error'"
                  class="change-icon"
                >
                  {{
                    percentageChange >= 0
                      ? 'mdi-trending-up'
                      : 'mdi-trending-down'
                  }}
                </v-icon>
                <span
                  :class="
                    percentageChange >= 0
                      ? 'positive-change'
                      : 'negative-change'
                  "
                >
                  {{ Math.abs(percentageChange).toFixed(1) }}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart container -->
        <div class="chart-container">
          <v-skeleton-loader
            v-if="loading"
            type="image"
            class="chart-skeleton"
          />
          <div v-else-if="!chartData.datasets.length" class="chart-placeholder">
            <v-icon size="64" color="grey lighten-2">mdi-chart-line</v-icon>
            <p class="placeholder-text">
              {{
                selectedMetric === 'orders'
                  ? $t('dashboard.chartNoDataOrders')
                  : selectedMetric === 'revenue'
                  ? $t('dashboard.chartNoDataRevenue')
                  : $t('dashboard.chartNoDataViews')
              }}
            </p>
          </div>
          <v2-line-chart
            v-else
            :chart-data="chartData"
            :options="chartOptions"
            :height="300"
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .performance-chart-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;
    @apply tw-w-full tw-overflow-hidden;

    .card-header {
      @apply tw-pb-2 tw-pt-3;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }
        }

        .header-right {
          .view-more-btn {
            @apply tw-text-orange-600;
            @apply tw-whitespace-nowrap;
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-0;
      @apply tw-overflow-x-auto;
    }

    .chart-controls,
    .chart-controls-skeleton {
      @apply tw-flex tw-items-center tw-justify-between tw-mb-4 tw-space-x-4;
      @apply tw-min-w-0;

      .metric-toggle {
        @apply tw-flex-shrink-0;
      }

      .controls-right {
        @apply tw-flex tw-items-center tw-space-x-3;
        @apply tw-min-w-0;

        .comparison-toggle {
          @apply tw-mt-0 tw-order-last sm:tw-order-first;

          ::v-deep .v-input__slot {
            @apply tw-mb-0;
          }
        }

        .timeframe-select {
          @apply tw-max-w-xs;
          @apply tw-min-w-0;
        }
      }
    }

    .summary-section {
      @apply tw-mb-4;

      .summary-card {
        @apply tw-rounded-lg tw-px-4 tw-py-2 tw-border tw-border-neutral-200;
        @apply tw-inline-block;

        .summary-content {
          @apply tw-flex tw-flex-col tw-items-start;

          .summary-value {
            @apply tw-text-2xl tw-font-bold;

            .summary-info-icon {
              @apply tw-ml-1 tw-text-amber-500 tw-cursor-help;
            }
          }

          .summary-change {
            @apply tw-flex tw-items-center tw-gap-1 tw-text-sm;

            .change-icon {
              @apply tw-mr-0;
            }

            .positive-change {
              @apply tw-text-green-600 tw-font-medium;
            }

            .negative-change {
              @apply tw-text-red-600 tw-font-medium;
            }
          }
        }
      }
    }

    .chart-container {
      @apply tw-bg-neutral-50 tw-rounded-lg tw-p-4;
      @apply tw-w-full tw-overflow-hidden;
      min-height: 300px;

      .chart-skeleton {
        ::v-deep .v-skeleton-loader__image {
          height: 300px;
        }
      }

      .chart-placeholder {
        @apply tw-flex tw-flex-col tw-items-center tw-justify-center;
        @apply tw-h-full tw-text-center tw-py-12;

        .placeholder-text {
          @apply tw-text-neutral-600 tw-font-medium tw-mt-4 tw-mb-2;
        }

        .placeholder-subtext {
          @apply tw-text-neutral-500 tw-text-sm;
        }
      }
    }
  }

  // Responsive adjustments
  @media (max-width: 640px) {
    .performance-chart-card {
      .card-header {
        @apply tw-flex-wrap tw-gap-2;

        .header-title {
          @apply tw-text-base;
        }

        .view-more-btn {
          @apply tw-text-sm;
        }
      }

      .chart-controls,
      .chart-controls-skeleton {
        @apply tw-flex-col tw-items-stretch tw-space-x-0 tw-space-y-2;

        .metric-toggle {
          @apply tw-w-full;

          ::v-deep .v-btn-toggle {
            @apply tw-w-full;
          }

          ::v-deep .v-btn {
            @apply tw-flex-1;
          }
        }

        .controls-right {
          @apply tw-flex-col tw-items-stretch tw-space-x-0 tw-space-y-2;
          @apply tw-w-full;

          .comparison-toggle {
            @apply tw-w-full;
          }

          .timeframe-select {
            @apply tw-max-w-none tw-w-full;
          }
        }
      }

      .summary-section {
        .summary-card {
          .summary-content {
            .summary-value {
              @apply tw-text-xl;
            }
          }
        }
      }
    }
  }
</style>
